import colorLib from "@kurkle/color";
import { defineComponent, PropType } from "vue";
import {
    buildDashboardHeatmapIntensities,
    DashboardHeatmapScale,
    DEFAULT_DASHBOARD_HEATMAP_OPTIONS,
    normalizeDashboardHeatmapValues,
    resolveDashboardHeatmapWindowBefore,
} from "../pages/dashboard/heatmap.js";

const Weekdays = [
    { short: "L", label: "Lundi" },
    { short: "M", label: "Mardi" },
    { short: "M", label: "Mercredi" },
    { short: "J", label: "Jeudi" },
    { short: "V", label: "Vendredi" },
    { short: "S", label: "Samedi" },
    { short: "D", label: "Dimanche" },
] as const;
const FullDateFormatter = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
});
const NumberFormatter = new Intl.NumberFormat("fr-FR");

function getCellColor(color: string, intensity: number) {
    if (intensity <= 0)
        return "rgba(255, 255, 255, .035)";

    try {
        return colorLib(color).alpha(intensity).rgbString();
    } catch {
        return `color-mix(in srgb, ${color} ${Math.round(intensity * 100)}%, transparent)`;
    }
}

function formatCellLabel(timestamp: number, bucketMs: number, value: number, seriesLabel: string, future: boolean) {
    const period = new Intl.DateTimeFormat("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        ...(bucketMs < 24 * 60 * 60_000 ? {
            hour: "2-digit",
            minute: "2-digit",
        } : {}),
    }).format(timestamp);
    const count = NumberFormatter.format(value);

    return future
        ? `${seriesLabel}, ${period} : à venir`
        : `${seriesLabel}, ${period} : ${count} événement${value === 1 ? "" : "s"}`;
}

export default defineComponent({
    name: "DashboardHeatmap",

    props: {
        values: {
            type: Array as PropType<number[]>,
            required: true,
        },
        color: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            default: "Événements",
        },
        before: {
            type: Number,
            default: () => Date.now(),
        },
        bucketMs: {
            type: Number,
            default: DEFAULT_DASHBOARD_HEATMAP_OPTIONS.bucketMs,
        },
        cellCount: {
            type: Number,
            default: DEFAULT_DASHBOARD_HEATMAP_OPTIONS.cellCount,
        },
        scale: {
            type: String as PropType<DashboardHeatmapScale>,
            default: DEFAULT_DASHBOARD_HEATMAP_OPTIONS.scale,
        },
        onselect: {
            type: Function as PropType<(index: number) => void>,
            default: null,
        },
    },

    setup(props) {
        return () => {
            const values = normalizeDashboardHeatmapValues(props.values, props.cellCount);
            const intensities = buildDashboardHeatmapIntensities(values, props.scale);
            const before = resolveDashboardHeatmapWindowBefore(props.before, props.bucketMs);
            const start = before - props.cellCount * props.bucketMs;
            const weekCount = Math.ceil(values.length / Weekdays.length);

            return (
                <div class="dashboard-heatmap">
                    <div class="dashboard-heatmap-calendar">
                        <div class="dashboard-heatmap-weekdays" role="row">
                            {Weekdays.map((weekday) => (
                                <span key={weekday.label} role="columnheader" aria-label={weekday.label}>{weekday.short}</span>
                            ))}
                        </div>

                        <div
                            class="dashboard-heatmap-grid"
                            role="grid"
                            aria-label={`${props.label}, activité sur ${weekCount} semaines`}
                            aria-colcount={Weekdays.length}
                            aria-rowcount={weekCount}
                        >
                            {values.map((value, index) => {
                                const timestamp = start + index * props.bucketMs;
                                const future = timestamp > Date.now();
                                const label = formatCellLabel(timestamp, props.bucketMs, value, props.label, future);
                                const className = {
                                    "dashboard-heatmap-cell": true,
                                    interactive: Boolean(props.onselect) && !future,
                                    "is-future": future,
                                };
                                const content = (
                                    <span class="dashboard-heatmap-tooltip" role="tooltip">
                                        <span>{FullDateFormatter.format(timestamp)}</span>
                                        <strong>
                                            {future
                                                ? "À venir"
                                                : `${NumberFormatter.format(value)} événement${value === 1 ? "" : "s"}`}
                                        </strong>
                                        <small>{props.label}</small>
                                    </span>
                                );

                                return !future && Boolean(props.onselect) ? (
                                    <button
                                        key={timestamp}
                                        type="button"
                                        class={className}
                                        style={{ background: getCellColor(props.color, intensities[index] || 0) }}
                                        aria-label={label}
                                        aria-rowindex={Math.floor(index / Weekdays.length) + 1}
                                        aria-colindex={(index % Weekdays.length) + 1}
                                        onClick={() => props.onselect!(index)}
                                    >
                                        {content}
                                    </button>
                                ) : (
                                    <div
                                        key={timestamp}
                                        class={className}
                                        style={{ background: getCellColor(props.color, intensities[index] || 0) }}
                                        role="gridcell"
                                        aria-label={label}
                                        aria-rowindex={Math.floor(index / Weekdays.length) + 1}
                                        aria-colindex={(index % Weekdays.length) + 1}
                                        tabindex={0}
                                    >
                                        {content}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div class="dashboard-heatmap-legend">
                        <span>Moins</span>
                        {[ 0, .18, .38, .58, .78, 1 ].map((intensity) => (
                            <i key={intensity} style={{ background: getCellColor(props.color, intensity) }} />
                        ))}
                        <span>Plus</span>
                        <small>
                            Activité relative · {weekCount} semaines · {props.bucketMs >= 24 * 60 * 60_000 ? "1 jour par case" : "1 période par case"}
                        </small>
                    </div>
                </div>
            );
        };
    },
});
